/**
 * DynamicFieldRenderer
 *
 * Renders a field-definition array into MUI inputs.
 * Used by task stage UI (execution) and previewed in the Workflow Builder.
 *
 * Props:
 *   fields  — array of { field_key, label, field_type, options, is_required, help_text, value }
 *   values  — { [field_key]: value } (controlled externally)
 *   onChange — (field_key, value) => void
 *   readOnly — boolean (preview / completed-stage view)
 */

import React from 'react';
import {
  Box, TextField, FormControlLabel, Switch, FormControl,
  InputLabel, Select, MenuItem, FormHelperText, Chip, Typography,
  OutlinedInput, Checkbox, ListItemText,
} from '@mui/material';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: { style: { maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP, width: 250 } },
};

const DynamicFieldRenderer = ({ fields = [], values = {}, onChange, readOnly = false }) => {
  const handleChange = (fieldKey, value) => {
    if (!readOnly && onChange) onChange(fieldKey, value);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {fields.map((field) => {
        const { field_key, label, field_type, options = [], is_required, help_text } = field;
        const value = values[field_key] ?? field.value ?? null;

        switch (field_type) {
          case 'text':
          case 'number':
            return (
              <TextField
                key={field_key}
                label={label}
                type={field_type === 'number' ? 'number' : 'text'}
                value={value ?? ''}
                onChange={e => handleChange(field_key, field_type === 'number' ? Number(e.target.value) : e.target.value)}
                required={is_required}
                helperText={help_text || ''}
                disabled={readOnly}
                fullWidth
                size="small"
              />
            );

          case 'textarea':
            return (
              <TextField
                key={field_key}
                label={label}
                multiline
                rows={3}
                value={value ?? ''}
                onChange={e => handleChange(field_key, e.target.value)}
                required={is_required}
                helperText={help_text || ''}
                disabled={readOnly}
                fullWidth
                size="small"
              />
            );

          case 'date':
            return (
              <TextField
                key={field_key}
                label={label}
                type="date"
                value={value ? (value.split('T')[0] || value) : ''}
                onChange={e => handleChange(field_key, e.target.value)}
                required={is_required}
                helperText={help_text || ''}
                disabled={readOnly}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            );

          case 'boolean':
            return (
              <Box key={field_key}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!value}
                      onChange={e => handleChange(field_key, e.target.checked)}
                      disabled={readOnly}
                    />
                  }
                  label={<>
                    {label}
                    {is_required && <Typography component="span" color="error"> *</Typography>}
                  </>}
                />
                {help_text && <FormHelperText sx={{ mt: -0.5 }}>{help_text}</FormHelperText>}
              </Box>
            );

          case 'select': {
            const parsedOptions = Array.isArray(options) ? options : (typeof options === 'string' ? JSON.parse(options) : []);
            return (
              <FormControl key={field_key} fullWidth size="small" required={is_required}>
                <InputLabel>{label}</InputLabel>
                <Select
                  value={value ?? ''}
                  label={label}
                  onChange={e => handleChange(field_key, e.target.value)}
                  disabled={readOnly}
                >
                  <MenuItem value=""><em>None</em></MenuItem>
                  {parsedOptions.map(opt => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </Select>
                {help_text && <FormHelperText>{help_text}</FormHelperText>}
              </FormControl>
            );
          }

          case 'multiselect': {
            const parsedOptions = Array.isArray(options) ? options : (typeof options === 'string' ? JSON.parse(options) : []);
            const selected = Array.isArray(value) ? value : [];
            return (
              <FormControl key={field_key} fullWidth size="small" required={is_required}>
                <InputLabel>{label}</InputLabel>
                <Select
                  multiple
                  value={selected}
                  input={<OutlinedInput label={label} />}
                  renderValue={(s) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {s.map(v => <Chip key={v} label={v} size="small" />)}
                    </Box>
                  )}
                  MenuProps={MenuProps}
                  onChange={e => handleChange(field_key, typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                  disabled={readOnly}
                >
                  {parsedOptions.map(opt => (
                    <MenuItem key={opt} value={opt}>
                      <Checkbox checked={selected.includes(opt)} />
                      <ListItemText primary={opt} />
                    </MenuItem>
                  ))}
                </Select>
                {help_text && <FormHelperText>{help_text}</FormHelperText>}
              </FormControl>
            );
          }

          case 'photo':
            // Photo rendering is handled by the stage's dedicated image strip in UnifiedTaskModal.
            // This renderer just shows a placeholder in preview mode.
            if (readOnly) {
              return (
                <Box key={field_key}>
                  <Typography variant="caption" color="text.secondary">{label} — photos managed in image strip below</Typography>
                </Box>
              );
            }
            return null;

          default:
            return (
              <TextField
                key={field_key}
                label={label}
                value={value ?? ''}
                onChange={e => handleChange(field_key, e.target.value)}
                required={is_required}
                helperText={help_text || ''}
                disabled={readOnly}
                fullWidth
                size="small"
              />
            );
        }
      })}
    </Box>
  );
};

export default DynamicFieldRenderer;
